import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { FileText, Upload, CheckCircle2, AlertCircle, Clock, Shield } from "lucide-react";

const DOC_TYPES = [
  { id: "drivers_license",     label: "Driver's License",      req: true },
  { id: "insurance",           label: "Auto Insurance (COI)",  req: true },
  { id: "vehicle_registration",label: "Vehicle Registration",  req: true },
  { id: "coi",                 label: "Commercial COI (Tow)",  req: false },
  { id: "background_check",    label: "Background Check Auth", req: false },
];

const STATUS_STYLE = {
  approved:        { bg: "bg-emerald-500", text: "text-white", label: "APPROVED" },
  pending_review:  { bg: "bg-amber-500",   text: "text-white", label: "IN REVIEW" },
  rejected:        { bg: "bg-asap-red",    text: "text-white", label: "REJECTED" },
  not_started:     { bg: "bg-slate-500",   text: "text-white", label: "NOT STARTED" },
};

export default function DriverDocumentsPanel({ approvalStatus, onChange }) {
  const [docs, setDocs] = useState([]);
  const [selType, setSelType] = useState("drivers_license");
  const [url, setUrl] = useState("");
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/driver/documents");
      setDocs(data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!url.trim()) { setErr("Paste a link to the document image or PDF"); return; }
    setBusy(true);
    setErr("");
    try {
      await api.post("/driver/documents", { doc_type: selType, doc_url: url.trim(), expires_on: expires });
      setUrl("");
      setExpires("");
      await load();
      onChange?.();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const status = approvalStatus || "not_started";
  const sty = STATUS_STYLE[status] || STATUS_STYLE.not_started;

  return (
    <div className="bg-asap-blue/30 backdrop-blur border-2 border-asap-yellow/40 p-4" data-testid="driver-docs-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.3em] text-asap-yellow font-bold flex items-center gap-1"><Shield size={12}/> Onboarding Docs</div>
        <div className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold tracking-widest ${sty.bg} ${sty.text}`} data-testid="approval-status">
          {status === "approved" && <CheckCircle2 size={10}/>}
          {status === "pending_review" && <Clock size={10}/>}
          {status === "rejected" && <AlertCircle size={10}/>}
          {sty.label}
        </div>
      </div>

      {status !== "approved" && (
        <div className="mt-2 p-2 bg-asap-red/20 border border-asap-red text-asap-yellow text-xs flex items-start gap-2">
          <AlertCircle size={14} className="shrink-0 mt-0.5"/>
          <span>An admin must approve your docs before you can go online. Upload license + insurance + registration.</span>
        </div>
      )}

      <div className="mt-3 space-y-2">
        <div className="text-[10px] uppercase tracking-widest font-bold text-asap-yellow">Add Document</div>
        <select value={selType} onChange={(e) => setSelType(e.target.value)} data-testid="doc-type" className="w-full px-2 py-2 bg-white/10 border border-asap-yellow/50 text-white text-sm outline-none">
          {DOC_TYPES.map((d) => <option key={d.id} value={d.id} className="text-asap-deep">{d.label}{d.req ? " *" : ""}</option>)}
        </select>
        <input placeholder="Link to image/PDF (e.g. drive/dropbox URL)" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="doc-url" className="w-full px-2 py-2 bg-white/10 border border-asap-yellow/50 text-white text-sm placeholder:text-white/40 outline-none"/>
        <input placeholder="Expires (YYYY-MM-DD, optional)" value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="doc-expires" className="w-full px-2 py-2 bg-white/10 border border-asap-yellow/50 text-white text-sm placeholder:text-white/40 outline-none"/>
        {err && <div className="text-asap-red text-xs font-bold">{err}</div>}
        <button onClick={submit} disabled={busy} data-testid="doc-submit" className="w-full px-3 py-2 bg-asap-yellow text-asap-deep font-display uppercase text-xs disabled:opacity-50 inline-flex items-center justify-center gap-1">
          <Upload size={14}/> {busy ? "Submitting…" : "Submit Document"}
        </button>
      </div>

      {docs.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-asap-yellow mb-1">Submitted ({docs.length})</div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-2 text-xs text-white/80 border-b border-white/10 py-1" data-testid={`doc-${d.doc_type}`}>
                <FileText size={12} className="text-asap-yellow shrink-0"/>
                <span className="flex-1 truncate">{DOC_TYPES.find((x) => x.id === d.doc_type)?.label || d.doc_type}</span>
                <a href={d.doc_url} target="_blank" rel="noreferrer" className="text-asap-yellow underline text-[10px]">view</a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
